import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Network, Crown, Pencil, Trash2, Filter, Gauge } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import Sidebar from '../components/Sidebar';
import ConflictGauge from '../components/ConflictGauge';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CharacterWeb() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [plotProjects, setPlotProjects] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedProtagonist, setSelectedProtagonist] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [showConflictGauge, setShowConflictGauge] = useState(null);
  const [formData, setFormData] = useState({
    moral_difference: '',
    tactical_difference: '',
    central_argument: '',
  });
  // null = creating; a relationship object = editing it
  const [editingRel, setEditingRel] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, projectsRes] = await Promise.all([
        authFetch(`${API}/auth/me`, { credentials: 'include' }),
        authFetch(`${API}/projects`, { credentials: 'include' })
      ]);

      const userData = await userRes.json();
      setUser(userData);

      const projectsData = await projectsRes.json();
      const charProjects = projectsData.filter(p => p.project_type === 'character');
      const plots = projectsData.filter(p => p.project_type === 'plot');
      setCharacters(charProjects);
      setPlotProjects(plots);

      if (['indie', 'studio'].includes(userData.subscription_tier)) {
        const relsRes = await authFetch(`${API}/relationships`, { credentials: 'include' });
        const relsData = await relsRes.json();
        setRelationships(relsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingRel(null);
    setFormData({ moral_difference: '', tactical_difference: '', central_argument: '' });
    setSelectedProtagonist('');
    setSelectedTarget('');
  };

  const openAdd = () => {
    closeModal();
    setShowAddModal(true);
  };

  const openEdit = (rel) => {
    setEditingRel(rel);
    setSelectedProtagonist(rel.protagonist_id);
    setSelectedTarget(rel.target_id);
    setFormData({
      moral_difference: rel.moral_difference || '',
      tactical_difference: rel.tactical_difference || '',
      central_argument: rel.central_argument || '',
    });
    setShowAddModal(true);
  };

  const handleAddRelationship = async (e) => {
    e.preventDefault();

    if (!selectedProtagonist || !selectedTarget) {
      toast.error('Please select both characters');
      return;
    }

    if (!formData.moral_difference || !formData.tactical_difference || !formData.central_argument) {
      toast.error('Please fill in all relationship fields');
      return;
    }

    try {
      const isEdit = Boolean(editingRel);
      const response = await authFetch(
        isEdit ? `${API}/relationships/${editingRel.relationship_id}` : `${API}/relationships`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            protagonist_id: isEdit ? editingRel.protagonist_id : selectedProtagonist,
            target_id: isEdit ? editingRel.target_id : selectedTarget,
            moral_difference: formData.moral_difference,
            tactical_difference: formData.tactical_difference,
            central_argument: formData.central_argument,
          }),
        }
      );

      if (response.status === 403) {
        navigate('/upgrade');
        return;
      }

      if (!response.ok) throw new Error('Request failed');

      toast.success(isEdit ? 'Relationship updated' : 'Relationship added');
      closeModal();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(editingRel ? 'Failed to update relationship' : 'Failed to add relationship');
    }
  };

  const handleDeleteRelationship = async (relId) => {
    if (!confirm('Delete this relationship?')) return;

    try {
      await authFetch(`${API}/relationships/${relId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      toast.success('Relationship deleted');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete relationship');
    }
  };

  const getCharacterName = (charId) => {
    const char = characters.find(c => c.project_id === charId);
    if (!char) return 'Unknown';
    // Return character name from level_0 if available, otherwise project title
    return char.level_0?.name || char.title;
  };
  
  // Get display name for dropdown options (Character Name or Project Title)
  const getCharacterDisplayName = (char) => {
    const name = char.level_0?.name;
    if (name) {
      return `${name} (${char.title})`;
    }
    return char.title;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading...</div>
      </div>
    );
  }

  if (!['indie', 'studio'].includes(user?.subscription_tier)) {
    return (
      <div className="min-h-screen bg-background text-foreground flex">
        <div className="noise-overlay" />
        <Sidebar userTier={user?.subscription_tier || 'spark'} />
        
        <div className="flex-1 flex flex-col">
          <header className="glassmorphism sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard?type=character')}
                className="rounded-none font-mono uppercase tracking-widest text-xs"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </header>

          <main className="flex-1 flex items-center justify-center px-4">
            <div className="max-w-2xl text-center bg-card border border-border/40 p-12">
              <Crown className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="font-mono text-3xl font-semibold mb-4">Indie Feature</h2>
              <p className="text-muted-foreground text-lg mb-8">
                The Character Web is an advanced tool for professional writer's rooms. Upgrade to Indie or above to map relationships and thematic arguments.
              </p>
              <Button
                onClick={() => navigate('/upgrade')}
                data-testid="upgrade-btn"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-8 py-4 shadow-glow"
              >
                Upgrade to Indie
              </Button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const protagonists = characters.filter(c => c.character_role === 'protagonist');
  const others = characters.filter(c => c.character_role !== 'protagonist');
  
  // Filter characters and relationships by selected project
  const filteredCharacters = selectedProject === 'all' 
    ? characters 
    : characters.filter(c => {
        // For now, we show all characters when a plot is selected
        // In the future, this could be linked via a project-character relationship
        return true;
      });
  
  const filteredRelationships = selectedProject === 'all'
    ? relationships
    : relationships.filter(rel => 
        filteredCharacters.some(c => c.project_id === rel.protagonist_id) ||
        filteredCharacters.some(c => c.project_id === rel.target_id)
      );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <div className="noise-overlay" />
      <Sidebar userTier={user?.subscription_tier || 'spark'} />

      <div className="flex-1 flex flex-col">
        <header className="glassmorphism sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard?type=character')}
              className="rounded-none font-mono uppercase tracking-widest text-xs"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <h1 className="font-mono text-2xl font-bold tracking-tighter">The Web</h1>
          </div>
        </header>

        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
          <div className="mb-12 text-center">
            <Network className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="font-mono text-3xl md:text-4xl font-semibold tracking-tight mb-4" data-testid="web-title">
              Character Relationship Matrix
            </h2>
            <p className="text-muted-foreground text-lg">
              Map how your characters push and pull against each other
            </p>
          </div>

          {/* Project Filter */}
          {plotProjects.length > 0 && (
            <div className="mb-8 bg-card border border-border/40 p-4">
              <div className="flex items-center gap-4">
                <Filter className="w-5 h-5 text-primary" />
                <label className="font-mono text-sm uppercase tracking-wider text-foreground">
                  Filter by Story Project:
                </label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  data-testid="project-filter"
                  className="flex-1 max-w-md bg-background border border-input rounded-none font-mono px-4 py-2 text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Characters</option>
                  {plotProjects.map(plot => (
                    <option key={plot.project_id} value={plot.project_id}>
                      {plot.title} ({plot.format})
                    </option>
                  ))}
                </select>
              </div>
              {selectedProject !== 'all' && (
                <p className="text-xs text-muted-foreground mt-2 ml-9">
                  Showing relationships for characters in "{plotProjects.find(p => p.project_id === selectedProject)?.title}"
                </p>
              )}
            </div>
          )}

          {characters.length < 2 ? (
            <div className="bg-card border border-border/40 p-12 text-center">
              <p className="text-muted-foreground">
                Create at least 2 characters to build relationship connections
              </p>
              <Button
                onClick={() => navigate('/create-project?type=character')}
                className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
              >
                Create Character
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-8 flex justify-end">
                <Button
                  onClick={openAdd}
                  data-testid="add-relationship-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Relationship
                </Button>
              </div>

              <div className="space-y-6">
                {filteredRelationships.length === 0 ? (
                  <div className="bg-card border border-border/40 p-8 text-center">
                    <p className="text-muted-foreground">No relationships mapped yet</p>
                  </div>
                ) : (
                  filteredRelationships.map((rel) => (
                    <div key={rel.relationship_id} className="bg-card border border-border/40 p-6" data-testid={`relationship-${rel.relationship_id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-mono text-lg font-semibold mb-1">
                            {getCharacterName(rel.protagonist_id)} ↔ {getCharacterName(rel.target_id)}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowConflictGauge(rel)}
                            data-testid={`conflict-gauge-${rel.relationship_id}`}
                            className="rounded-none text-xs font-mono"
                          >
                            <Gauge className="w-4 h-4 mr-1" />
                            Gauge
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(rel)}
                            aria-label="Edit relationship"
                            data-testid={`edit-rel-${rel.relationship_id}`}
                          >
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRelationship(rel.relationship_id)}
                            data-testid={`delete-rel-${rel.relationship_id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="font-mono text-xs uppercase text-primary mb-2">Moral Difference</p>
                          <p className="text-sm text-foreground">{rel.moral_difference}</p>
                        </div>
                        <div>
                          <p className="font-mono text-xs uppercase text-primary mb-2">Tactical Difference</p>
                          <p className="text-sm text-foreground">{rel.tactical_difference}</p>
                        </div>
                        <div>
                          <p className="font-mono text-xs uppercase text-primary mb-2">Central Argument</p>
                          <p className="text-sm text-foreground">{rel.central_argument}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Add Relationship Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={closeModal}>
          <div className="bg-card border border-primary/30 p-8 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-mono text-2xl font-semibold mb-6">{editingRel ? 'Edit Relationship' : 'Add Relationship'}</h3>

            <form onSubmit={handleAddRelationship} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block font-mono text-sm uppercase">Character A</label>
                  <select
                    value={selectedProtagonist}
                    onChange={(e) => setSelectedProtagonist(e.target.value)}
                    disabled={!!editingRel}
                    className={`w-full bg-background border border-input rounded-none font-mono px-3 py-2 ${editingRel ? 'opacity-60 cursor-not-allowed' : ''}`}
                    data-testid="select-character-a"
                    required
                  >
                    <option value="">Select character</option>
                    {characters.map(c => (
                      <option key={c.project_id} value={c.project_id}>
                        {getCharacterDisplayName(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block font-mono text-sm uppercase">Character B</label>
                  <select
                    value={selectedTarget}
                    onChange={(e) => setSelectedTarget(e.target.value)}
                    disabled={!!editingRel}
                    className={`w-full bg-background border border-input rounded-none font-mono px-3 py-2 ${editingRel ? 'opacity-60 cursor-not-allowed' : ''}`}
                    data-testid="select-character-b"
                    required
                  >
                    <option value="">Select character</option>
                    {characters.filter(c => c.project_id !== selectedProtagonist).map(c => (
                      <option key={c.project_id} value={c.project_id}>
                        {getCharacterDisplayName(c)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block font-mono text-sm uppercase">Moral Difference</label>
                <AutoExpandTextarea
                  value={formData.moral_difference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, moral_difference: e.target.value }))}
                  placeholder="What fundamentally different core values do they hold?"
                  className="bg-background border-input rounded-none font-mono"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block font-mono text-sm uppercase">Tactical Difference</label>
                <AutoExpandTextarea
                  value={formData.tactical_difference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, tactical_difference: e.target.value }))}
                  placeholder="How do their methods differ?"
                  className="bg-background border-input rounded-none font-mono"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block font-mono text-sm uppercase">Central Argument</label>
                <AutoExpandTextarea
                  value={formData.central_argument}
                  onChange={(e) => setFormData(prev => ({ ...prev, central_argument: e.target.value }))}
                  placeholder="What is the central debate they are having throughout the story?"
                  className="bg-background border-input rounded-none font-mono min-h-[100px]"
                  required
                />
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1 rounded-none font-mono uppercase tracking-widest text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs"
                >
                  {editingRel ? 'Save Changes' : 'Add Relationship'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Conflict Gauge Modal */}
      {showConflictGauge && (
        <ConflictGauge
          relationshipId={showConflictGauge.relationship_id}
          charAName={getCharacterName(showConflictGauge.protagonist_id)}
          charBName={getCharacterName(showConflictGauge.target_id)}
          isPro={['indie', 'studio'].includes(user?.subscription_tier)}
          onClose={() => setShowConflictGauge(null)}
        />
      )}
    </div>
  );
}
